/**
 * Course Groups Configuration
 * 
 * This defines how courses are grouped and displayed on the website.
 * Each group can have multiple language variants or types.
 */

// Language codes and their display names
export const LANGUAGES = {
  'da': { name: 'Dansk', code: 'da', suffix: '' },
  'en': { name: 'Engelsk', code: 'en', suffix: 'E' },
  'ar': { name: 'Arabisk', code: 'ar', suffix: 'A' },
  'uk': { name: 'Ukrainsk', code: 'uk', suffix: 'UKR' },
  'fa': { name: 'Persisk', code: 'fa', suffix: 'P' }
};

// Note shown for courses held in collaboration with UCplus
export const UCPLUS_NOTE = 'Vi samarbejder med UCplus, der afholder følgende kurser på vores adresse.';

// Course ids that are held in collaboration with UCplus
export const UCPLUS_COURSE_IDS = ['vb-gu', 'vb-eu', 'taxa', 'evs'];

// Course ids with a LIVE UCplus hold integration (counts/holds come from UCplus API)
export const UCPLUS_LIVE_COURSE_IDS = ['vb-gu', 'taxa'];

// BAB types - Flextrafik uddannelser
export const BAB_TYPES = {
  'BAB-1': { name: 'BAB-1', price: 882, description: 'Introduktion til offentlig servicetrafik', duration: '1 dag', requirement: 'Ingen krav' },
  'BAB-2': { name: 'BAB-2', price: 2032, description: 'Befordring af sygdoms- og alderssvækkede passagerer', duration: '2 dage', requirement: 'Ingen krav' },
  'BAB-3A': { name: 'BAB-3A', price: 2971, description: 'Befordring af handicappede med liftbil', duration: '2 dage', requirement: 'Krav om gennemført BAB-1 og BAB-2' },
  'BAB-3B': { name: 'BAB-3B', price: 2971, description: 'Befordring af fysisk handicappede med trappemaskin', duration: '2 dage', requirement: 'Krav om gennemført BAB-3A' },
  'BAB-4': { name: 'BAB-4', price: 4312, description: 'Ajourføring af chauffører i offentlig servicetrafik', duration: '3 dage', requirement: 'Du skal have taget BAB modul 2 eller dit BAB 4-bevis skal fornyes (holder max 4 år).' },
  'BAB-1+2': { name: 'BAB-1 + BAB-2', price: 882 + 2032, description: 'Kombineret introduktion og befordring af svækkede passagerer', duration: '3 dage', requirement: 'Ingen krav' },
  'BAB-1-3A': { name: 'BAB-1 til BAB-3A', price: 882 + 2032 + 2971, description: 'Komplet pakke: BAB-1, BAB-2 og BAB-3A', duration: '5 dage', requirement: 'Ingen krav' },
};

// Course groups configuration
export const COURSE_GROUPS = [
  {
    id: 'vognmand-lastbil',
    slug: 'vognmand-lastbil',
    title: 'Vognmand til Lastbil',
    shortTitle: 'Vognmand Lastbil',
    // `seoTitle` bruges som <title> i stedet for den automatiske "navn + pris"-titel,
    // når søgeordene for siden ikke står i kursusnavnet. Må højst være 60 tegn.
    seoTitle: 'Godsvognmandsuddannelse, vognmandskursus lastbil | Votra',
    description: 'Godsvognmandsuddannelsen kvalificerer dig til at blive godkendt transportleder i en virksomhed med tilladelse til godskørsel for fremmed regning med lastbil eller vogntog over 3.500 kg. Du gennemfører forløbet som selvstudie med online kursusmateriale, der dækker samtlige emner i bilag 1 til bekendtgørelsen om godskørsel, og går derefter til prøven hos Votra, der er godkendt prøveudbyder hos Færdselsstyrelsen. Tre prøveforsøg er inkluderet i prisen.',
    shortDescription: 'Online selvstudie + godsvognmandsprøven hos Votra, godkendt prøvested.',
    requirements: [
      'Du skal have kendskab til et regnearksprogram, f.eks. Excel.',
      'Du skal være fyldt 18 år.'
    ],
    image: 'https://customer-assets.emergentagent.com/job_75937e85-5d04-4ca2-9da1-90520b393fdf/artifacts/j7660w1k_vogmand%20last.png',
    category: 'vognmand',
    hasLanguageVariants: true,
    prices: {
      da: { standard: 14900, jobcenter: 0 },
      fremmedsprog: { standard: 16900, jobcenter: 0 }
    },
    duration: 'Selvstudie, eget tempo',
    selfStudy: true,
    examIncluded: true,
    examAttempts: 3,
    examVenues: ['Ringsted', 'Søborg'],
    syllabus: 'bilag1'
  },
  {
    id: 'vognmand-varebil',
    slug: 'vognmand-varebil',
    title: 'Vognmand til Varebil',
    shortTitle: 'Vognmand Varebil',
    description: 'Vognmandsuddannelsen til varebil kvalificerer dig til tilladelse til godskørsel for fremmed regning med varebil eller vogntog mellem 2.500 kg og 3.500 kg tilladt totalvægt. Du gennemfører forløbet som selvstudie med online kursusmateriale, der dækker samtlige emner i bilag 2 til bekendtgørelsen om godskørsel, og går derefter til prøven hos Votra. Tre prøveforsøg er inkluderet i prisen.',
    shortDescription: 'Online selvstudie + varebilsvognmandsprøven hos Votra.',
    requirements: [
      'Du skal have kendskab til et regnearksprogram, f.eks. Excel.',
      'Du skal være fyldt 18 år.'
    ],
    image: 'https://customer-assets.emergentagent.com/job_b2f3dc70-989c-401b-bdd0-71079a67ea5a/artifacts/55rs0nvp_ad.png',
    category: 'vognmand',
    hasLanguageVariants: true,
    prices: {
      da: { standard: 6900, jobcenter: 0 },
      fremmedsprog: { standard: 8900, jobcenter: 0 }
    },
    duration: 'Selvstudie, eget tempo',
    selfStudy: true,
    examIncluded: true,
    examAttempts: 3,
    examVenues: ['Ringsted', 'Søborg'],
    syllabus: 'bilag2'
  },
  {
    id: 'vb-gu',
    slug: 'vb-gu',
    ucplusPartner: true,
    // UCplus' adresse, ikke Votras egen. Den skal navngives, så en læser (og en
    // AI-model) ikke tror, Votra har en afdeling i Skovlunde.
    location: 'Skovlunde (UCplus, Mileparken)',
    title: 'Varebil Grunduddannelse (VB GU)',
    shortTitle: 'Varebil Grunduddannelse',
    description: 'Grunduddannelse for varebilschauffører. Få de nødvendige kompetencer og dit kvalifikationsbevis til at køre varebil professionelt. Kurset dækker sikkerhed, køreteknik, lovgivning og kundeservice.',
    shortDescription: 'Grunduddannelse for varebilschauffører, få dit kvalifikationsbevis.',
    requirementsNote: 'Der er ingen krav eller forudsætninger for at deltage på kurset.',
    image: 'https://customer-assets.emergentagent.com/job_75937e85-5d04-4ca2-9da1-90520b393fdf/artifacts/yfbtqwwz_vb%20gu.png',
    category: 'chauffør',
    hasLanguageVariants: true,
    prices: {
      da: { standard: 3765, jobcenter: 0 },
      fremmedsprog: { standard: 4185, jobcenter: 0 }
    },
    amuPrice: 654,
    fullPrice: 3765,
    duration: '3 dage'
  },
  {
    id: 'vb-eu',
    slug: 'vb-eu',
    ucplusPartner: true,
    title: 'Varebil Efteruddannelse (VB EU)',
    shortTitle: 'Varebil Efteruddannelse',
    description: 'Efteruddannelse for varebilschauffører. Forny dit kvalifikationsbevis med dette lovpligtige kursus. Opdater dine kompetencer inden for sikkerhed, nye regler og bedste praksis.',
    shortDescription: 'Efteruddannelse for varebilschauffører, forny dit kvalifikationsbevis.',
    requirements: [
      'Gyldigt VUB-bevis.'
    ],
    requirementsNote: 'Er dit VUB-bevis udløbet, skal du vælge 2 dages efteruddannelse, og du må ikke genoptage den erhvervsmæssige kørsel med varebil, før du har gennemført efteruddannelsen.',
    image: 'https://customer-assets.emergentagent.com/job_75937e85-5d04-4ca2-9da1-90520b393fdf/artifacts/maz1g639_VB%20EU.png',
    category: 'chauffør',
    hasLanguageVariants: true,
    prices: {
      da: { standard: 2607, jobcenter: 0 },
      fremmedsprog: { standard: 2887, jobcenter: 0 }
    },
    amuPrice: 436,
    fullPrice: 2607,
    duration: '2 dage'
  },
  {
    id: 'bab',
    slug: 'bab',
    title: 'BAB - Flextrafik Uddannelse',
    shortTitle: 'BAB (Flextrafik)',
    description: 'Befordring af Bevægelseshæmmede (BAB), også kaldet Flextrafik. Bliv certificeret til at transportere passagerer med særlige behov. Kurset dækker kørestolshåndtering, førstehjælp, kommunikation og servicekørsel.',
    shortDescription: 'Flextrafikuddannelse, bliv certificeret til befordring af bevægelseshæmmede.',
    image: 'https://customer-assets.emergentagent.com/job_75937e85-5d04-4ca2-9da1-90520b393fdf/artifacts/oiklxfnz_flextrafik.png',
    category: 'flextrafik',
    hasLanguageVariants: false,
    hasTypeVariants: true,
    types: ['BAB-1', 'BAB-2', 'BAB-3A', 'BAB-3B', 'BAB-4', 'BAB-1+2', 'BAB-1-3A'],
    prices: BAB_TYPES,
    duration: 'Varierer efter type',
    requirementsNote: 'Kravene afhænger af, hvilken BAB-type du vælger, se kravene ud for hver type nedenfor.'
  },
  {
    id: 'taxa',
    slug: 'taxa',
    ucplusPartner: true,
    title: 'Taxa Uddannelse',
    shortTitle: 'Taxa',
    seoTitle: 'Taxakursus og taxauddannelse, bliv taxachauffør | Votra',
    description: 'Bliv professionel taxachauffør. Kurset giver dig alle de nødvendige kompetencer til at køre taxa lovligt i Danmark. Inkluderer lovgivning, kundeservice, sikkerhed, navigation og praktisk kørsel.',
    shortDescription: 'Komplet taxachaufføruddannelse, bliv certificeret taxachauffør.',
    requirements: [
      'Du skal være fyldt 21 år, når du aflægger de afsluttende prøver.',
      'Du skal have haft kørekort til kategori B i mindst 3 år, perioder med frakendelse tæller ikke med.',
      'Du skal dokumentere dansk på niveau med Prøve i Dansk 2 (eller folkeskolens prøve i dansk, 9. klasse).',
      'Du skal have en godkendt lægeattest og opfylde helbredskravene for "gruppe 2" (bl.a. syn og hørelse).',
      'Færdselsstyrelsen vurderer din vandel ud fra en straffeattest til offentligt brug, strafbare forhold afskærer dig ikke automatisk.'
    ],
    requirementsNote: 'Bemærk: På taxa er der ekstra omkostninger, chaufførkort (udstedes af Færdselsstyrelsen) på 1.700 kr. og lægeattest på 750 kr.',
    image: 'https://customer-assets.emergentagent.com/job_75937e85-5d04-4ca2-9da1-90520b393fdf/artifacts/qs89quo3_taxa.png',
    category: 'taxa',
    hasLanguageVariants: false,
    prices: {
      da: { standard: 13699, jobcenter: 0 }
    },
    amuPrice: 2180,
    fullPrice: 13698.85,
    duration: '10 dage',
    syllabus: 'bilag3'
  },
  {
    id: 'evs',
    slug: 'evs',
    ucplusPartner: true,
    title: 'EVS - Evakueringsstol',
    shortTitle: 'Evakueringsstol',
    description: 'Lær at anvende evakueringsstol korrekt og sikkert. Kurset giver dig de nødvendige kompetencer til at evakuere personer med nedsat mobilitet via trapper.',
    shortDescription: 'Kursus i evakueringsstol, sikker evakuering af personer.',
    requirements: [
      'Krav om gennemført BAB-3B.'
    ],
    image: 'https://customer-assets.emergentagent.com/job_75937e85-5d04-4ca2-9da1-90520b393fdf/artifacts/7780qcsi_Evak.png',
    category: 'specialkursus',
    hasLanguageVariants: false,
    hasTypeVariants: false,
    prices: {
      da: { standard: 1383, jobcenter: 0 }
    },
    duration: '1 dag',
    modules: 1
  },
  {
    id: 'ai-anvendelse',
    slug: 'ai-anvendelse',
    segment: 'it-ai',
    isNew: true,
    title: 'Anvendelse af Kunstig Intelligens (AI)',
    shortTitle: 'Kunstig Intelligens (AI)',
    description: 'På 6 uger lærer du at bygge AI-agenter, udvikle workflows og arbejde målrettet med kunstig intelligens. Kurset kombinerer tre af de mest efterspurgte AI-platforme i ét samlet forløb: ChatGPT Business, Microsoft 365 Copilot og Google Gemini Pro. Du lærer ikke kun, hvad værktøjerne kan, men hvordan du bruger dem til at løse virkelige opgaver i en arbejdskontekst. Du afslutter med et konkret projekt, der dokumenterer dine kompetencer.',
    shortDescription: 'Byg AI-agenter og workflows med ChatGPT, Copilot og Gemini, på 6 uger.',
    requirementsNote: 'Der er ingen krav eller forudsætninger for at deltage på kurset.',
    content: [
      'Generativ AI og ansvarlig brug, forstå hvordan AI fungerer, og skriv effektive prompts',
      'ChatGPT Business, skab tekst og billeder, byg GPT-agenter og automatisér opgaver',
      'Workflows med AI, integrér AI i dine daglige arbejdsprocesser',
      'Microsoft 365 Copilot, møderefarater, dataanalyse i Excel og Copilot-agenter',
      'AI-agenter, lav genbrugelige løsninger til dine gentagne arbejdsopgaver',
      'Google Gemini Pro, billeder og video, AI-agenter, NotebookLM og Deep Research'
    ],
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?crop=entropy&cs=srgb&fm=jpg&q=80&w=800&h=500&fit=crop',
    category: 'kunstig-intelligens',
    hasLanguageVariants: false,
    itPricing: { jobcenter: 29300 },
    prices: {
      da: { standard: 29300, jobcenter: 0 }
    },
    duration: '6 uger'
  },
  {
    id: 'office-chatgpt-ai',
    slug: 'office-chatgpt-ai',
    segment: 'it-ai',
    isNew: true,
    title: 'Microsoft Office, ChatGPT & AI-Værktøjer',
    shortTitle: 'MS Office, ChatGPT & AI',
    description: 'Få det bedste fra de klassiske Office-værktøjer kombineret med de nyeste AI-teknologier. På 6 uger får du solid viden om Excel, Word og PowerPoint og lærer, hvordan AI-værktøjer integreres direkte i Microsoft 365. Du bliver i stand til at arbejde mere effektivt, automatisere rutiner og skabe indhold, der løfter kvaliteten af dit arbejde, klar til at bruge din viden fra dag ét på jobbet.',
    shortDescription: 'Excel, Word, PowerPoint kombineret med ChatGPT, Copilot og AI-værktøjer.',
    requirementsNote: 'Der er ingen krav eller forudsætninger for at deltage på kurset.',
    content: [
      'Microsoft Excel, grundlæggende og avancerede funktioner, formler, pivottabeller og grafer',
      'Microsoft 365: Word, PowerPoint-design og fildeling i SharePoint',
      'ChatGPT Plus, prompting, omskrivning af tekst og oprettelse af GPT\'er',
      'Copilot, i søgemaskiner, i Office-pakken og sammen med andre Microsoft-tjenester',
      'Microsoft Power BI, byg dashboards og datamodeller fra flere datakilder',
      'AI-værktøjer: AI-billeder, AI-lyd og AI-video til professionelt brug'
    ],
    image: 'https://customer-assets.emergentagent.com/job_b2f3dc70-989c-401b-bdd0-71079a67ea5a/artifacts/m59f9lhd_C.png',
    category: 'kunstig-intelligens',
    hasLanguageVariants: false,
    itPricing: { jobcenter: 29300 },
    prices: {
      da: { standard: 29300, jobcenter: 0 }
    },
    duration: '6 uger'
  },
  {
    id: 'claude-ai-ivaerksaetter',
    slug: 'claude-ai-ivaerksaetter',
    segment: 'it-ai',
    isNew: true,
    title: 'Claude AI Iværksætterforløb',
    shortTitle: 'Claude AI Iværksætter',
    description: 'Gå fra idé til en konkret, digitalt understøttet virksomhed på 6 uger. Med udgangspunkt i en realistisk iværksætter-case lærer du at bruge Claude AI som din digitale sparringspartner til at udvikle forretningsidéer, skrive tekster, opbygge salgsflows, strukturere opgaver og automatisere rutiner. Forløbet er særligt målrettet dig, der ønsker at starte egen virksomhed inden for transport, service, håndværk, logistik eller administration. Du afslutter med en konkret digital forretningspakke.',
    shortDescription: 'Fra idé til egen virksomhed med Claude AI som digital sparringspartner.',
    requirementsNote: 'Der er ingen krav eller forudsætninger for at deltage på kurset.',
    content: [
      'Claude AI og digitalt iværksætteri',
      'Fundamentet for en virksomhed, forretningsprofil og struktur',
      'Hjemmeside og digital tilstedeværelse',
      'Bogføring, administration og budget',
      'E-mail, kundeservice og professionel kommunikation',
      'Leads, salg og kunderejse + Google Analytics',
      'AI-workflows og automatisering',
      'Afsluttende projekt, din digitale forretningspakke'
    ],
    image: 'https://customer-assets.emergentagent.com/job_b2f3dc70-989c-401b-bdd0-71079a67ea5a/artifacts/z2ondjuc_l.png',
    category: 'kunstig-intelligens',
    hasLanguageVariants: false,
    itPricing: { jobcenter: 29300 },
    prices: {
      da: { standard: 29300, jobcenter: 0 }
    },
    duration: '6 uger'
  }
];

// Helper function to get price for a course
export const getCoursePrice = (courseGroup, language = 'da', babType = null) => {
  if (courseGroup.hasTypeVariants && babType) {
    // BAB course - price by type
    const typeInfo = BAB_TYPES[babType];
    return typeInfo ? { standard: typeInfo.price, jobcenter: 0 } : { standard: 0, jobcenter: 0 };
  }
  
  if (courseGroup.hasLanguageVariants) {
    // Language variant course - da = dansk, anything else = fremmedsprog
    return language === 'da' ? courseGroup.prices.da : courseGroup.prices.fremmedsprog;
  }
  
  return courseGroup.prices?.da || { standard: 0, jobcenter: 0 };
};

// Helper to get language name from suffix
export const getLanguageFromSuffix = (suffix) => {
  if (!suffix) return LANGUAGES.da;
  const found = Object.values(LANGUAGES).find(l => l.suffix === suffix);
  return found || LANGUAGES.da;
};

// Helper to get course group by slug
export const getCourseGroupBySlug = (slug) => {
  return COURSE_GROUPS.find(g => g.slug === slug);
};

export default COURSE_GROUPS;
